import React from 'react'
import { Box, Flex, Portal, Spinner, Text } from '@chakra-ui/core'
import { AlertCircle, CheckCircle2, X } from 'lucide-react'
import useLocaleText from 'components/useLocaleText'
import type { ILiteral } from 'utils/localeText'
import { Button } from '../Button'
import styles from './styles.module.css'

export type ProgressStatus = 'uploading' | 'success' | 'error'

export interface IProgressToastProps {
  // A total of zero means the operation is still determining the amount of work.
  done: number
  total: number
  // Lifecycle: in-progress (spinner + neutral), success (green), error (red).
  status?: ProgressStatus
  // Locale keys per state (defaults suit uploads; override for download/save/etc.).
  labelKey?: string | ILiteral
  successKey?: string
  errorKey?: string
  isRtl?: boolean
  // In-progress accent; success/error use their own colours.
  color?: string
  onCancel?: () => void
}

// Fixed corner progress for batch operations. The caller owns operation state and dismissal.
const ProgressToastComponent: React.FC<IProgressToastProps> = ({
  done,
  total,
  status = 'uploading',
  labelKey = 'uploading_post_media_loading',
  successKey = 'alert_success',
  errorKey = 'alert_error',
  isRtl = false,
  color = '#0F172A',
  onCancel,
}) => {
  const cfg = {
    uploading: { bg: '#f8fafc', border: '#e2e8f0', accent: color, key: labelKey, Icon: null },
    success: { bg: '#ecfdf5', border: '#a7f3d0', accent: '#059669', key: successKey, Icon: CheckCircle2 },
    error: { bg: '#fef2f2', border: '#fecaca', accent: '#dc2626', key: errorKey, Icon: AlertCircle },
  }[status]
  const label = useLocaleText(cfg.key)
  const cancelLabel = useLocaleText('cancel')

  const hasTotal = total > 0
  const completed = Math.min(Math.max(0, done), Math.max(0, total))
  const pct = hasTotal ? (completed / total) * 100 : 0
  const isIndeterminate = status === 'uploading' && !hasTotal
  const count = `${completed} / ${total}`

  return (
    <Portal>
      <Box
        className={styles.toast}
        position="fixed"
        bottom="24px"
        left={isRtl ? '24px' : undefined}
        right={isRtl ? undefined : '24px'}
        zIndex={4000}
        bg={cfg.bg}
        border="1px solid"
        borderColor={cfg.border}
        borderRadius="12px"
        boxShadow="0 10px 28px rgba(15,23,42,0.16)"
        p="14px 16px"
        minWidth="240px"
        maxWidth="calc(100vw - 48px)"
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <Flex alignItems="center" style={{ gap: '10px' }} mb="10px">
          {cfg.Icon ? (
            <cfg.Icon size={18} color={cfg.accent} style={{ flexShrink: 0 }} />
          ) : (
            <Spinner size="sm" color={cfg.accent} thickness="2px" speed="0.6s" flexShrink={0} />
          )}
          <Text fontSize="13px" fontWeight={600} color="#1e293b" flex="1" minWidth="0" role="status">
            {label}
            {status === 'uploading' && hasTotal && (
              <React.Fragment>
                {' '}
                <bdi dir="ltr" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {count}
                </bdi>
              </React.Fragment>
            )}
          </Text>
          {status === 'uploading' && onCancel && (
            <Button
              variant="icon"
              type="button"
              w="24px"
              minW="24px"
              h="24px"
              p="0"
              flexShrink={0}
              aria-label={cancelLabel}
              title={cancelLabel}
              onClick={onCancel}
            >
              <X size={15} />
            </Button>
          )}
        </Flex>
        <Box
          height="6px"
          bg="rgba(15,23,42,0.06)"
          borderRadius="999px"
          overflow="hidden"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={hasTotal ? total : undefined}
          aria-valuenow={hasTotal ? (status === 'success' ? total : completed) : undefined}
          aria-valuetext={hasTotal && status === 'uploading' ? count : label}
        >
          <Box
            className={isIndeterminate ? styles.indeterminate : styles.fill}
            height="100%"
            bg={cfg.accent}
            borderRadius="999px"
            width={isIndeterminate ? '36%' : `${status === 'success' ? 100 : pct}%`}
          />
        </Box>
      </Box>
    </Portal>
  )
}

export const ProgressToast = React.memo(ProgressToastComponent)
